import React from 'react';
import { motion } from 'framer-motion';

// Fade up animation for sections
export const FadeUp = ({ children, delay = 0, className = '' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Staggered text reveal - each word appears
export const StaggeredText = ({ text, className = '', tag = 'div' }) => {
  const words = text.split(' ');
  const Tag = tag;

  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const child = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      className={`inline ${className}`}
    >
      {words.map((word, index) => (
        <motion.span
          key={index}
          variants={child}
          className="inline-block mr-[0.25em]"
        >
          {word}
        </motion.span>
      ))}
    </motion.div>
  );
};

// Line by line reveal for large headings
export const LineReveal = ({ lines, className = '' }) => {
  const container = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const lineVariant = {
    hidden: { 
      opacity: 0, 
      y: 100,
      clipPath: 'inset(100% 0 0 0)'
    },
    visible: {
      opacity: 1,
      y: 0,
      clipPath: 'inset(0% 0 0 0)',
      transition: {
        duration: 0.8,
        ease: [0.25, 0.1, 0.25, 1],
      },
    },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-50px' }}
      className={className}
    >
      {lines.map((line, index) => (
        <div key={index} className="overflow-hidden">
          <motion.div variants={lineVariant}>
            {line}
          </motion.div>
        </div>
      ))}
    </motion.div>
  );
};

// Scroll triggered word animation
export const ScrollWords = ({ words, className = '' }) => {
  return (
    <div className={className}>
      {words.map((word, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 100 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.8 }}
          transition={{ 
            duration: 0.6, 
            delay: index * 0.1,
            ease: [0.25, 0.1, 0.25, 1] 
          }}
          className="overflow-hidden"
        >
          <span className="block">{word}</span>
        </motion.div>
      ))}
    </div>
  );
};

// Parallax image
export const ParallaxImage = ({ src, alt, className = '' }) => {
  return (
    <motion.div
      className={`overflow-hidden ${className}`}
      initial={{ scale: 1.1 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <motion.img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        initial={{ y: -30 }}
        whileInView={{ y: 30 }}
        viewport={{ once: true }}
        transition={{ duration: 1.5, ease: 'linear' }}
      />
    </motion.div>
  );
};

// Fade in from side
export const FadeInSide = ({ children, direction = 'left', delay = 0, className = '' }) => {
  const xOffset = direction === 'left' ? -50 : 50;
  
  return (
    <motion.div
      initial={{ opacity: 0, x: xOffset }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: '-100px' }}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default FadeUp;
